import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { getEvidenceById } from '../lib/evidence';
import { FANOUT_PAGES } from '../lib/site';

const page = FANOUT_PAGES.find((p) => p.path === '/acompanhamento-e-queda-de-pontos');
const hensel = getEvidenceById('hensel-2007');
const feeney = getEvidenceById('feeney-2016');
const galasko = getEvidenceById('galasko-1993');

const mdc95 = feeney.results.minimumDetectableChange95;
const reliableRange = hensel.results.reliableChangeRange.join('–');
const practiceEffect = String(galasko.results.practiceEffectMean).replace('.', ',');

const ANSWER =
  `Não existe um número universal. Uma queda de ${mdc95} pontos aparece como mudança mínima detectável em alguns estudos, mas o significado depende da população, do intervalo entre aplicações e das condições do teste. O correto é interpretar a mudança junto do desempenho basal, da comparabilidade das avaliações e da evolução funcional.`;

const REFERENCES = [hensel, feeney, galasko].map(
  (study) => `${study.citation} ${study.primarySource.kind.toUpperCase()}: ${study.primarySource.value}.`
);

const RELATED = [
  {
    to: '/interpretacao-dos-resultados',
    label: 'Como interpretar o resultado do Mini Mental',
    note: 'entenda como o escore ganha significado quando combinado com contexto e funcionalidade.'
  },
  {
    to: '/como-aplicar',
    label: 'Como aplicar o Mini Mental',
    note: 'padronizar a aplicação é essencial para comparar avaliações ao longo do tempo.'
  },
  {
    to: '/escolaridade',
    label: 'Mini Mental por escolaridade',
    note: 'escolaridade e efeito teto alteram a leitura do desempenho.'
  },
  {
    to: '/mini-mental-ou-moca',
    label: 'Mini Mental ou MoCA',
    note: 'veja limitações do MEEM quando a suspeita clínica envolve alterações leves.'
  },
  {
    to: '/diagnostica-alzheimer',
    label: 'O Mini Mental diagnostica Alzheimer?',
    note: 'trajetória do escore não determina etiologia.'
  }
];

function InlineLink({ to, children }) {
  return <Link to={to} className=" text-[var(--primary)] hover:underline">{children}</Link>;
}

export default function AcompanhamentoEQuedaDePontos() {
  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="tres-pontos">
        <h2 id="tres-pontos" className="text-2xl mb-5">{mdc95} pontos de queda são significativos?</h2>
        <p className="text-[var(--text-muted)] mb-5">
          Às vezes, mas não universalmente. O número aparece em estudos de confiabilidade e erro de medida, não como uma constante biológica do MEEM. População, intervalo entre aplicações e método estatístico mudam a interpretação.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead className="bg-[var(--surface-soft)]">
              <tr>
                <th className="p-3 border border-[var(--border)]">Estudo</th>
                <th className="p-3 border border-[var(--border)]">População</th>
                <th className="p-3 border border-[var(--border)]">Intervalo</th>
                <th className="p-3 border border-[var(--border)]">Estimativa</th>
                <th className="p-3 border border-[var(--border)]">Leitura correta</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-3 border border-[var(--border)] font-bold">Hensel 2007</td>
                <td className="p-3 border border-[var(--border)]">{hensel.population}</td>
                <td className="p-3 border border-[var(--border)]">{hensel.retestInterval}</td>
                <td className="p-3 border border-[var(--border)]">{reliableRange} pontos, IC {hensel.results.confidenceInterval}%</td>
                <td className="p-3 border border-[var(--border)]">Faixa de mudança confiável dependente da comparação; não é regra clínica universal.</td>
              </tr>
              <tr>
                <td className="p-3 border border-[var(--border)] font-bold">Feeney 2016</td>
                <td className="p-3 border border-[var(--border)]">{feeney.population}</td>
                <td className="p-3 border border-[var(--border)]">{feeney.retestInterval}</td>
                <td className="p-3 border border-[var(--border)]">MDC95: {mdc95} pontos</td>
                <td className="p-3 border border-[var(--border)]">Mudança mínima detectável no desenho estudado; não equivale automaticamente a mudança clinicamente importante.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section aria-labelledby="variacao-sem-piora">
        <h2 id="variacao-sem-piora" className="text-2xl mb-5">Por que o mesmo paciente pode variar sem ter piorado?</h2>
        <div className="grid gap-4">
          <div className="p-4 border border-[var(--border)] rounded-xl"><strong>Erro de medida.</strong> Nenhum teste é perfeitamente estável entre duas aplicações; parte da diferença pode ser ruído.</div>
          <div className="p-4 border border-[var(--border)] rounded-xl"><strong>Regressão à média.</strong> Um resultado excepcionalmente baixo ou alto tende a se aproximar da média em nova medida, mesmo sem mudança clínica real.</div>
          <div className="p-4 border border-[var(--border)] rounded-xl"><strong>Efeito de prática.</strong> Repetir o instrumento pode melhorar discretamente o desempenho por familiaridade. Em {galasko.population}, {galasko.followUp}, Galasko et al. estimaram aumento médio de {practiceEffect} ponto entre as primeiras sessões.</div>
          <div className="p-4 border border-[var(--border)] rounded-xl"><strong>Condições diferentes.</strong> Dor, fadiga, ansiedade, sedação, horário, ambiente e mudança de aplicador podem alterar a pontuação.</div>
        </div>
        <p className="text-[var(--text-muted)] mt-5">
          Por isso, a comparação longitudinal exige aplicação tão semelhante quanto possível. Veja <InlineLink to="/como-aplicar">como aplicar o Mini Mental de forma padronizada</InlineLink>.
        </p>
      </section>

      <section aria-labelledby="queda-pequena">
        <h2 id="queda-pequena" className="text-2xl mb-5">Quando uma queda pequena merece atenção?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Uma mudança pequena ganha importância quando acompanha perda funcional nova, relato consistente do paciente ou informante, alteração cognitiva ou comportamental coerente, ou uma tendência repetida em avaliações comparáveis.
        </p>
        <p className="text-[var(--text-muted)]">
          Nesses casos, a relevância não vem de cruzar um número mágico: vem da concordância entre trajetória do escore e trajetória clínica. Uma oscilação isolada, sem correlato funcional, pede revisão das condições e nova avaliação antes de conclusões.
        </p>
      </section>

      <section aria-labelledby="estabilidade-exclui">
        <h2 id="estabilidade-exclui" className="text-2xl mb-5">Pontuação estável exclui declínio?</h2>
        <p className="text-[var(--text-muted)] mb-4"><strong>Não.</strong> Estabilidade do escore não garante estabilidade clínica.</p>
        <p className="text-[var(--text-muted)]">
          Efeito teto e sensibilidade limitada para alterações leves podem fazer o MEEM permanecer estável apesar de mudança percebida no funcionamento. Quando história e número divergem, veja <InlineLink to="/interpretacao-dos-resultados">como interpretar o resultado no contexto clínico</InlineLink> e, se a questão for sensibilidade para comprometimento leve, <InlineLink to="/mini-mental-ou-moca">Mini Mental ou MoCA</InlineLink>.
        </p>
      </section>

      <section aria-labelledby="comparar-aplicacoes">
        <h2 id="comparar-aplicacoes" className="text-2xl mb-5">Como comparar duas aplicações corretamente</h2>
        <ol className="space-y-3 text-[var(--text-muted)] list-decimal pl-5">
          <li>Confirme que foi usada a mesma versão/instrumento.</li>
          <li>Verifique se a aplicação foi padronizada.</li>
          <li>Registre data e intervalo entre as avaliações.</li>
          <li>Registre aplicador e condições relevantes da avaliação.</li>
          <li>Considere o escore basal e a possibilidade de efeito teto.</li>
          <li>Confronte a variação com funcionalidade e história clínica.</li>
        </ol>
      </section>

      <section aria-labelledby="fluxo-pratico">
        <h2 id="fluxo-pratico" className="text-2xl mb-5">Fluxo prático</h2>
        <div className="p-5 border border-[var(--border)] rounded-2xl bg-[var(--surface-soft)] font-bold leading-relaxed">
          mudança no escore → comparabilidade das aplicações → magnitude + intervalo → funcionalidade → contexto clínico → aprofundar ou acompanhar
        </div>
        <p className="text-[var(--text-muted)] mt-5">Esse fluxo organiza a leitura longitudinal; não é um algoritmo diagnóstico e não atribui causa ao declínio.</p>
      </section>
    </ArticleLayout>
  );
}
